<template>
  <div class="project-container">
    <header class="header">
      <div class="flex-container">
        <div class="image-container">
          <img :src="require('@/assets/image/pikuTradeLogo.png')" alt="Logo PikuTrade" class="header-image" loading="lazy"/>
        </div>
        <div class="text-container">
          <h1>{{ $t('pikutrade.title') }}</h1>
          <p class="subtitle">{{ $t('pikutrade.subtitle') }}</p>
        </div>
      </div>
    </header>

    <section class="about-project">
      <h2>{{ $t('pikutrade.aboutTitle') }}</h2>
      <div class="content-grid">
        <div class="text-block">
          <p v-html="$t('pikutrade.about1')"></p>
          <p v-html="$t('pikutrade.about2')"></p>
        </div>
        <div class="highlight-box">
           <div class="stat-item">
             <span class="mdi mdi-chart-line stat-icon"></span>
             <span class="stat-text">Real-Time Trading</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-robot-outline stat-icon"></span>
             <span class="stat-text">AI Powered Analysis</span>
           </div>
           <div class="stat-item">
             <span class="mdi mdi-lightning-bolt-outline stat-icon"></span>
             <span class="stat-text">Async API Batching</span>
           </div>
        </div>
      </div>
    </section>

    <section class="architecture-details">
      <h2>Architecture & Composants</h2>
      <div class="tech-grid">
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-cog-outline tech-icon"></span>
            <h4>API Backend (Spring Boot)</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-package-variant-closed feature-icon"></span>
              <p v-html="$t('pikutrade.api1')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-brain feature-icon"></span>
              <p v-html="$t('pikutrade.api2')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-trending-up feature-icon"></span>
              <p v-html="$t('pikutrade.api3')"></p>
            </div>
          </div>
        </div>
        
        <div class="tech-card">
          <div class="tech-header">
            <span class="mdi mdi-palette-outline tech-icon"></span>
            <h4>Frontend (Vue.js)</h4>
          </div>
          <div class="feature-list">
            <div class="feature-item">
              <span class="mdi mdi-chart-bell-curve-cumulative feature-icon"></span>
              <p v-html="$t('pikutrade.frontend1')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-swap-horizontal feature-icon"></span>
              <p v-html="$t('pikutrade.frontend2')"></p>
            </div>
            <div class="feature-item">
              <span class="mdi mdi-view-dashboard-outline feature-icon"></span>
              <p v-html="$t('pikutrade.frontend3')"></p>
            </div>
          </div>
        </div>
      </div>
    </section>



    <section class="visuals-section">
      <h2>Aperçus du Projet</h2>
      <div class="visuals-grid">
        <div class="visual-card">
          <h3>{{ $t('pikutrade.apiIllustration') }}</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/api-endpoints.png')" alt="Exemples de requêtes API" class="doc-image" loading="lazy"/>
          </div>
        </div>
        <div class="visual-card">
          <h3>{{ $t('pikutrade.chartTitle') }}</h3>
          <div class="image-wrapper">
            <img :src="require('@/assets/image/realtime-chart.png')" alt="Graphique en temps réel" class="doc-image" loading="lazy"/>
          </div>
        </div>
      </div>
    </section>

    <section class="technical-details">
      <h2>{{ $t('pikutrade.learningsTitle') }}</h2>
      <div class="competencies-grid">
        <InfraCompetencyCard 
          icon="mdi-cube-outline"
          title="Architecture SOLID"
          :description="$t('pikutrade.learning1')"
        />
        <InfraCompetencyCard 
          icon="mdi-clock-fast"
          title="Asynchronisme Java"
          :description="$t('pikutrade.learning2')"
        />
        <InfraCompetencyCard 
          icon="mdi-webpack"
          title="WebSockets & Temps Réel"
          :description="$t('pikutrade.learning3')"
        />
        <InfraCompetencyCard 
          icon="mdi-robot-outline"
          title="Intégration IA"
          :description="$t('pikutrade.learning4')"
        />
      </div>
    </section>
  </div>
</template>

<script lang="ts">
import {Vue, Component} from 'vue-facing-decorator';
import InfraCompetencyCard from '@/component/project/infra/InfraCompetencyCard.vue';

@Component({
  components: {
    InfraCompetencyCard
  }
})
export default class PikuTradeComponent extends Vue {
}
</script>

<style scoped>
.project-container {
  font-family: 'TTInterfaces', sans-serif;
  line-height: 1.7;
  color: var(--ln-text-primary);
  background-color: transparent;
  width: 100%;
  max-width: var(--ln-content-max-width);
  margin: 0 auto;
  padding: 2rem 1.5rem;
  box-sizing: border-box;
}

.header {
  text-align: center;
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-elevated);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-lg);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 12px 48px var(--ln-glass-shadow);
}

.flex-container {
  display: flex;
  align-items: center;
  gap: 3rem;
  flex-wrap: wrap;
  justify-content: center;
}

.image-container {
  width: 160px;
  flex-shrink: 0;
}

.text-container {
  flex: 1 1 400px;
  text-align: left;
}

.header h1 {
  font-size: clamp(2rem, 6vw, 3rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 0.5rem;
  letter-spacing: -1px;
}

.subtitle {
  font-size: 1.25rem;
  color: var(--ln-text-secondary);
  font-weight: 500;
  opacity: 0.9;
}

.header-image {
  width: 100%;
  max-width: 160px;
  border-radius: 50%;
  box-shadow: 0 10px 30px rgba(0,0,0,0.15);
  transition: transform 0.5s ease;
}

.header-image:hover {
  transform: scale(1.05) rotate(5deg);
}

section {
  margin-bottom: 2rem;
  padding: 2.5rem;
  background: var(--ln-glass-base);
  backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  -webkit-backdrop-filter: blur(var(--ln-glass-blur)) saturate(180%);
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  box-shadow: 0 8px 32px var(--ln-glass-shadow);
  transition: all var(--ln-transition-normal) var(--ln-ease-smooth);
}

section:hover {
  transform: translateY(-5px);
  box-shadow: 0 15px 50px var(--ln-glass-shadow-elevated);
}

h2 {
  font-size: clamp(1.5rem, 5vw, 2.2rem);
  font-weight: 700;
  color: var(--ln-text-primary);
  margin-bottom: 2rem;
  position: relative;
  padding-bottom: 1rem;
}

h2::after {
  content: '';
  position: absolute;
  bottom: 0;
  left: 0;
  width: 80px;
  height: 4px;
  background: linear-gradient(to right, var(--ln-teal-deep), var(--ln-terracotta-subtle));
  border-radius: 2px;
}

.content-grid {
  display: grid;
  grid-template-columns: 1.5fr 1fr;
  gap: 3rem;
  align-items: center;
}

.highlight-box {
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border-strong);
}

.stat-item {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.stat-item:last-child { margin-bottom: 0; }
.stat-icon { font-size: 2.2rem; color: var(--ln-teal-deep); }
.stat-text { font-weight: 600; color: var(--ln-text-primary); }

.tech-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.tech-card {
  display: flex;
  flex-direction: column;
  background: var(--ln-glass-surface);
  padding: 2rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
}

.tech-header {
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 2rem;
  padding-bottom: 1rem;
  border-bottom: 1px solid var(--ln-glass-border);
}

.tech-icon { font-size: 2.5rem; color: var(--ln-teal-deep); }
.tech-header h4 { margin: 0; color: var(--ln-text-primary); font-size: 1.4rem; }

.feature-list {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
}

.feature-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;
}

.feature-icon {
  font-size: 1.5rem;
  color: var(--ln-teal-light);
  background: var(--ln-glass-elevated);
  padding: 0.5rem;
  border-radius: 50%;
  box-shadow: inset 0 2px 4px var(--ln-glass-shadow);
  flex-shrink: 0;
}

.feature-item p {
  margin: 0;
  font-size: 0.95rem;
  color: var(--ln-text-secondary);
  line-height: 1.6;
}

/* Visuals grid */
.visuals-grid {
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 2rem;
}

.visual-card {
  display: flex;
  flex-direction: column;
}

.visual-card h3 {
  margin-bottom: 1.5rem;
  color: var(--ln-text-primary);
  font-size: 1.2rem;
  text-align: center;
}

.image-wrapper {
  background: var(--ln-glass-surface);
  padding: 1.5rem;
  border-radius: var(--ln-radius-md);
  border: 1px solid var(--ln-glass-border);
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
}

.doc-image {
  width: 100%;
  height: 100%;
  object-fit: cover; /* Changed from contain to cover for better filling, or stay contain if it crops badly, but contain is safer for charts */
  border-radius: 8px;
  box-shadow: 0 8px 24px var(--ln-glass-shadow);
  transition: transform 0.4s var(--ln-ease-smooth);
}

/* Ensure images don't stretch weirdly but fill nicely */
.visual-card:first-child .doc-image {
  object-fit: contain;
  max-height: 300px;
}

.visual-card:last-child .doc-image {
  object-fit: cover;
  max-height: 300px;
}

.doc-image:hover {
  transform: scale(1.03);
}

/* Competencies */
.intro-text { margin-bottom: 2rem; color: var(--ln-text-secondary); }

.competencies-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 1rem;
}

@media (max-width: 1200px) {
  .competencies-grid { grid-template-columns: repeat(2, 1fr); }
}

@media (max-width: 992px) {
  .content-grid, .tech-grid, .visuals-grid { grid-template-columns: 1fr; }
}

@media (max-width: 768px) {
  .header { padding: 1.5rem; }
  .flex-container { gap: 1rem; flex-direction: column; }
  .text-container { text-align: center; flex: 1 1 auto; }
  section { padding: 1.5rem; }
  .visuals-grid { display: flex; flex-direction: column; }
  .competencies-grid { grid-template-columns: 1fr; }
}
</style>
